import React from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { isHost } from 'venuescanner-js/constants/user_type';

import EnquiryDetails from 'venuescanner-js/react/components/Inbox/components/Enquiry/EnquiryDetails';
import EnquiryEditForm from 'venuescanner-js/react/components/Inbox/components/Enquiry/EditForm/EnquiryEditForm';
import QuoteEditForm from 'venuescanner-js/react/components/Inbox/components/Enquiry/EditForm/QuoteEditForm';
import EnquiryDecline from 'venuescanner-js/react/components/Inbox/components/Enquiry/EnquiryDecline';
import EnquirySettingsInfo from 'venuescanner-js/react/components/Inbox/components/Enquiry/EnquirySettingsInfo';
import reasons from 'venuescanner-js/constants/decline-reasons.js';
import EnquirySuccess from './EnquirySuccess';
import RequestQuote from './RequestQuote';
import ContactDetails from './ContactDetails';
import ConfirmBookingPrice from './ConfirmBookingPrice';
import StripePromo from './StripePromo';
import { LoadingPanel } from './LoadingPanel';
import SubscriptionPromo from './SubscriptionPromo';

const EnquiryContainer = ({ state, send, status }) => {
  const { thread, organiser, venue, concierge } = state.context;
  const { can } = thread || { can: {} };

  const isSubscribedHost = isHost(thread) && !!venue.sub_plan;

  if (thread.concierge || isSubscribedHost) {
    const user = thread.concierge ? concierge : organiser;

    return (
      <section className='enquiry-panel inbox__section inbox__section--no-background'>
        <ContactDetails user={user} thread={thread} />
        <div className='enquiry-panel__enquiry-wrapper'>
          <div className='enquiry-panel__enquiry-wrapper-enquiry'>
            <Enquiry status={status} can={can} state={state} send={send} />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className='enquiry-panel inbox__section'>
      <Enquiry status={status} can={can} state={state} send={send} />
    </section>
  );
};

EnquiryContainer.propTypes = {
  state: PropTypes.object.isRequired,
  send: PropTypes.func.isRequired,
  status: PropTypes.string
};

const Enquiry = ({ can, state, send, status }) => {
  const { successDelay, cancellationReason, thread, space, venue } =
    state.context;

  switch (true) {
    case state.matches('editQuote'):
      return (
        <QuoteEditForm
          thread={thread}
          venue={venue}
          send={send}
          onSubmit={quote => send({ type: 'SEND_QUOTE', quote })}
          onCancel={() => send({ type: 'BACK' })}
          onMarkAsBooked={() => send({ type: 'MARK_AS_BOOKED' })}
          onCloseEnquiry={() => send({ type: 'CLOSE' })}
        />
      );
    case state.matches('editEvent'):
      return (
        <EnquiryEditForm
          thread={thread}
          onSubmit={eventDetails =>
            send({ type: 'SEND_EVENT_DETAILS', details: eventDetails })
          }
          onCancel={() => send({ type: 'BACK' })}
        />
      );
    case state.matches('reopen'):
      return isHost(thread) ? (
        <QuoteEditForm
          thread={thread}
          venue={venue}
          send={send}
          onMarkAsBooked={() => send({ type: 'MARK_AS_BOOKED' })}
          onCloseEnquiry={() => send({ type: 'CLOSE' })}
          onSubmit={quote =>
            send({ type: 'REOPENING_CONFIRMED', enquiry: quote })
          }
          onCancel={() => send({ type: 'BACK' })}
        />
      ) : (
        <EnquiryEditForm
          thread={thread}
          onSubmit={enquiry => send({ type: 'REOPENING_CONFIRMED', enquiry })}
          onCancel={() => send({ type: 'BACK' })}
        />
      );

    case state.matches('reopeningEnquiry'):
    case state.matches('submittingQuote'):
    case state.matches('submittingQuoteRequest'):
    case state.matches('submittingEventDetails'):
    case state.matches('cancellingThread'):
    case state.matches('unmarkingAsBooked'):
    case state.matches('confirmingPrice'):
    case state.matches('markingAsBooked'):
      return <LoadingPanel />;
    case state.matches('markingAsBookedSuccess'):
      return (
        <EnquirySuccess
          animated
          transitionTime={successDelay}
          header='Booking confirmed!'
          text='Congratulations on another booking!'
          subText='We’ve updated your dashboard with all the relevant details. '
        />
      );

    case state.matches('requestQuote'):
      return <RequestQuote thread={thread} send={send} venue={venue} />;

    case state.matches('enquiryEditSuccess'):
      return (
        <EnquirySuccess
          transitionTime={successDelay}
          header='Done!'
          text='Details successfully changed'
        />
      );

    case state.matches('quoteEditSuccess'):
      return (
        <EnquirySuccess
          transitionTime={successDelay}
          header="We've sent it!"
          text='We’ve sent all the quote details to the organiser.'
          subText='Once they have reviewed the details and confirmed, we’ll let you know.'
        />
      );

    case state.matches('quoteRequestedSuccess'):
      return (
        <EnquirySuccess
          transitionTime={successDelay}
          header="We've sent it!"
          text="Your booking request was sent to the host and it's awaiting their response"
        />
      );

    case state.matches('stripePromo'):
      return (
        <StripePromo venue={venue} onClick={() => send({ type: 'SKIP' })} />
      );
    case state.matches('subscriptionPromo'):
      return <SubscriptionPromo onClick={() => send({ type: 'SKIP' })} />;

    case state.matches('confirmCancellation'):
      return (
        <EnquiryDecline
          thread={thread}
          onSubmit={reason => {
            send({
              type: reason ? 'SET_SETTINGS' : 'CANCELLATION_CONFIRMED',
              reason,
            });
          }}
          onCancel={() => send({ type: 'BACK' })}
        />
      );

    case state.matches('setSettings'):
      return (
        <EnquirySetSetting
          space={space}
          venue={venue}
          onSubmit={() => send({ type: 'CANCELLATION_CONFIRMED' })}
          cancellationReason={cancellationReason}
        />
      );

    case state.matches('confirmBookingPrice'):
      return <ConfirmBookingPrice send={send} />;

    case state.matches('summary'): {
      return <Redirect to={`/inbox/${thread._id.toString()}/summary`} />;
    }

    default:
      return (
        <EnquiryDetails
          status={status}
          thread={thread}
          space={space}
          can={can}
          send={send}
          state={state}
        />
      );
  }
};

Enquiry.propTypes = {
  can: PropTypes.object.isRequired,
  send: PropTypes.func.isRequired,
  state: PropTypes.shape({
    context: PropTypes.shape({
      successDelay: PropTypes.any,
      cancellationReason: PropTypes.any,
      thread: PropTypes.shape({
        _id: PropTypes.oneOfType([
          PropTypes.shape({
            toString: PropTypes.func,
          }),
          PropTypes.string,
        ]),
      }).isRequired,
      space: PropTypes.object.isRequired,
      venue: PropTypes.object.isRequired,
    }),
    matches: PropTypes.func.isRequired,
  }),
  status: PropTypes.string,
};

function EnquirySetSetting({ space, venue, cancellationReason, onSubmit }) {
  const { _id: spaceId } = space.data();

  return (
    <>
      {cancellationReason === reasons.PRICE && (
        <EnquirySettingsInfo
          title='Get better enquiries by adding accurate pricing'
          copy='You can update your prices, including having different prices for different days of the week, in the Flexible Pricing box in the Manage Venue section of your account.'
          settings={{
            pathname: `/manage/spaces/${venue._id}/${spaceId}/pricing`,
            copy: 'Update pricing',
          }}
          setShowSettingsInfo={onSubmit}
        />
      )}
      {cancellationReason === reasons.AVAILABILITY && (
        <EnquirySettingsInfo
          title="Add your availability to stop receiving enquiries for days your venue's unavailable"
          copy='You can mark your venue as unavailable for hire on certain days in the Calendar.'
          settings={{
            pathname: '/calendar/opening-hours',
            copy: 'Update availability',
          }}
          setShowSettingsInfo={onSubmit}
        />
      )}
      {cancellationReason === reasons.SUITABILITY && (
        <EnquirySettingsInfo
          title="Get more relevant enquiries by selecting the event types you're interested in"
          copy='Make sure the list of event types for your venue is up to date to receive more relevant enquiries.'
          settings={{
            pathname: `/manage/spaces/${venue._id}/${spaceId}/space_tags`,
            copy: 'Update types',
          }}
          setShowSettingsInfo={onSubmit}
        />
      )}
    </>
  );
}

EnquirySetSetting.propTypes = {
  space: PropTypes.shape({
    data: PropTypes.func.isRequired,
  }).isRequired,
  venue: PropTypes.shape({
    _id: PropTypes.string.isRequired,
  }).isRequired,
  cancellationReason: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export function ErrorMessage({ children }) {
  return <p className='error-message'>{children}</p>;
}

ErrorMessage.propTypes = {
  children: PropTypes.node.isRequired,
};
export default EnquiryContainer;
